import { isVideoTestimonialMission } from '../constants';
import type { ConditionalLogic } from './evaluateConditionalLogic';

export type SurveyQuestion = {
  id?: number;
  surveyID?: number;
  uuid?: string | null;
  text: string;
  type: string;
  options: unknown[];
  scale: string;
  isRequired?: boolean;
  conditionalLogic?: ConditionalLogic;
};

export type TestimonialQuestion = {
  uuid?: string | null;
  text: string;
  shortQuestion: string | null;
  recordingMode: string;
  description: string | null;
  position?: number;
};

export type NormalizedQuestion = SurveyQuestion | TestimonialQuestion;

// gloss
// why: the formatter must accept both API question shapes (question vs text) - missing one
// why: silently collapses every question to its default placeholder card.
export const formatSurveyQuestionFromApi = (q: Record<string, unknown>): SurveyQuestion => {
  const rawOptions = q.options ?? q.questionOptions;
  let parsedOptions: unknown[] = [];
  if (rawOptions) {
    try {
      parsedOptions = typeof rawOptions === 'string' ? JSON.parse(rawOptions) : (rawOptions as unknown[]);
    } catch {
      parsedOptions = [];
    }
  }
  return {
    id: q.surveyID as number,
    uuid: (q.uuid as string) || null,
    text: (q.text as string) || (q.question as string) || '',
    type: (q.type as string) || (q.questionType as string) || 'text',
    options: parsedOptions,
    scale: q.scale ? String(q.scale) : '10',
    isRequired: Boolean(q.isRequired ?? true),
    conditionalLogic: (q.conditionalLogic as ConditionalLogic) ?? null,
  };
};

// gloss
export const formatTestimonialQuestionFromApi = (q: Record<string, unknown>): TestimonialQuestion => ({
  uuid: (q.uuid as string) || null,
  text: (q.question as string) || '',
  shortQuestion: (q.compiledQuestion as string) || null,
  recordingMode: (q.recordingMode as string) || 'camera',
  description: (q.description as string) || null,
  position: q.position as number,
});

// gloss
export const formatQuestionFromUrlParams = (
  q: Record<string, unknown>,
  normalizedMissionType?: string,
): NormalizedQuestion => {
  const isTestimonial = normalizedMissionType ? isVideoTestimonialMission(normalizedMissionType) : false;

  const base = {
    ...(q.id || q.surveyID ? { id: q.id || q.surveyID, surveyID: q.id || q.surveyID } : {}),
    ...(q.uuid ? { uuid: q.uuid } : {}),
    text: (q.text as string) || '',
    shortQuestion: isTestimonial ? (q.compiledQuestion as string) || null : (q.shortQuestion as string)?.trim() || null,
    recordingMode: (q.recordingMode as string) || 'camera',
    description: (q.description as string)?.trim() || null,
  };

  if (isTestimonial) return base as TestimonialQuestion;

  return {
    ...base,
    type: (q.type as string) || 'text',
    options: (q.options as unknown[]) || [],
    scale: q.scale ? String(q.scale) : '10',
    isRequired: Boolean(q.isRequired ?? true),
    conditionalLogic: (q.conditionalLogic as ConditionalLogic) ?? null,
  } as SurveyQuestion;
};
